import * as d3 from 'd3';

import React, { useEffect, useRef, useState } from 'react';
import {
  addBarGroup,
  addColumnNames,
  addGradient,
  addScaleLines,
  chartHeight,
  getScaleHeight,
  notationPositions,
  resizeBars,
  resizeColumnNames,
  resizeNotations,
  resizeScaleLines,
} from '../utils/netZeroChartHelper';
import {
  animateBars,
  getBarData,
  navigateBreakdownSteps,
} from '../utils/netZeroHelper';

import { GraphTooltip } from './';
import PropTypes from 'prop-types';
import { light } from '../theme/colors';
import styled from 'styled-components';

const GraphWrapper = styled.div`
  padding: 60px 0 20px;
  position: relative;

  @media screen and (max-width: 780px) {
    padding: 30px 0 10px;
  }
`;

const GraphSvg = styled.svg`
  width: 100%;

  rect {
    transition:
      y 1s ease-in-out,
      height 1s ease-in-out;
  }

  rect.notation-background-small,
  text.notation-text-small {
    display: none;
  }

  @media screen and (max-width: 780px) {
    margin-top: 20px;

    rect.notation-background-large,
    text.notation-text-large {
      display: none;
    }

    rect.notation-background-small,
    text.notation-text-small {
      display: block;
    }
  }
`;

const GraphNotation = styled.div`
  display: none;
  font-size: 12px;
  margin: 0 16px;
  border-top: 1px solid ${light};
  padding-top: 12px;

  strong {
    margin-right: 5px;
  }

  @media screen and (max-width: 780px) {
    display: block;
  }
`;

const NetZeroChart = ({
  currentStep,
  breakdownSteps,
  heatingMethod,
  interactiveBreakdown,
  interactive,
  hideNotations,
  cars,
}) => {
  const wrapperRef = useRef(null);
  const svgRef = useRef(null);
  const [svgDrawn, setSvgDrawn] = useState(false);
  const [displayedStep, setDisplayedStep] = useState(-1);
  const [zeroY, setZeroY] = useState(undefined);
  const [tHeight, setTHeight] = useState(undefined);
  const [displayedInteractiveBreakdown, setDisplayedInteractiveBreakdown] =
    useState(undefined);

  const onResize = () => {
    const svgWidth = wrapperRef?.current?.offsetWidth;
    resizeScaleLines(svgWidth);
    resizeBars(svgWidth);
    resizeColumnNames(svgWidth);
    resizeNotations(svgWidth);
  };

  useEffect(() => {
    if (!svgDrawn) {
      // draw the chart
      const svgWidth = wrapperRef.current.offsetWidth;
      const svg = d3
        .select(svgRef.current)
        .attr('width', svgWidth)
        .attr('height', chartHeight)
        .on('mousemove', event => {
          const { layerX, layerY } = event;
          const tooltip = d3.select('#tooltip');
          const tooltipHeight = tooltip.node().getBoundingClientRect().height;
          const tooltipWidth = tooltip.node().getBoundingClientRect().width;
          tooltip
            .style('left', `${layerX - tooltipWidth / 2}px`)
            .style('top', `${layerY - (tooltipHeight - 40)}px`);
        });

      // work out min and max tonnes
      let max = 0;
      const { electricity, heating, car } = breakdownSteps[1];
      const { solar } = breakdownSteps[2];
      [electricity, heating, car].forEach(value => {
        if (value > max) {
          max = Math.ceil(value);
        }
      });
      const min = Math.floor(solar);
      const range = max + -min;
      const tHeight = getScaleHeight(range);
      setTHeight(tHeight);

      // define chart elements
      const defs = svg.append('defs');
      addGradient(defs, 'elec-gradient', '#e5098b', '#ff594f');
      addGradient(defs, 'heat-gradient', '#0259ab', '#19d5ff');
      addGradient(defs, 'car-gradient', '#629c10', '#74b816');
      addGradient(defs, 'solar-gradient', '#f8af1f', '#f7d22d');

      const zeroY = addScaleLines(svg, range, tHeight, max, svgWidth);
      setZeroY(zeroY);

      addColumnNames(svg, svgWidth);

      const newBarData = getBarData(
        tHeight,
        breakdownSteps[1],
        breakdownSteps[currentStep],
      );

      addBarGroup(
        svg,
        newBarData.electricity,
        zeroY,
        'elec',
        0,
        'Electricity CO2 emissions',
        svgWidth,
      );
      addBarGroup(
        svg,
        newBarData.heating,
        zeroY,
        'heat',
        1,
        'Heating CO2 emissions',
        svgWidth,
      );
      addBarGroup(
        svg,
        newBarData.car,
        zeroY,
        'car',
        2,
        'Car CO2 emissions',
        svgWidth,
      );
      addBarGroup(
        svg,
        newBarData.solar,
        zeroY,
        'solar',
        3,
        'Solar',
        svgWidth,
        true,
        notationPositions.above,
      );

      if (interactive) {
        setDisplayedInteractiveBreakdown(interactiveBreakdown);
        animateBars(
          getBarData(tHeight, breakdownSteps[1], breakdownSteps[6]),
          zeroY,
        );
      }

      d3.select('.zero-y-line').raise();

      setSvgDrawn(true);
    }
    window.addEventListener('resize', onResize);
  }, [
    svgDrawn,
    breakdownSteps,
    currentStep,
    interactive,
    interactiveBreakdown,
  ]);

  if (interactive && interactiveBreakdown && !!zeroY) {
    if (
      JSON.stringify(interactiveBreakdown) !==
      JSON.stringify(displayedInteractiveBreakdown)
    ) {
      animateBars(
        getBarData(tHeight, breakdownSteps[1], interactiveBreakdown),
        zeroY,
      );
      setDisplayedInteractiveBreakdown(interactiveBreakdown);
    }
  }
  if (!interactive && displayedStep !== currentStep && !!zeroY) {
    setDisplayedStep(currentStep);
    const barData = getBarData(
      tHeight,
      breakdownSteps[1],
      breakdownSteps[currentStep],
    );
    navigateBreakdownSteps(
      currentStep,
      barData,
      zeroY,
      wrapperRef,
      heatingMethod,
      cars,
    );
  }

  return (
    <GraphWrapper ref={wrapperRef}>
      <GraphTooltip id="tooltip" />
      <GraphSvg ref={svgRef} id="netzero-chart" width="100%" />
      {!hideNotations && <GraphNotation id="notation-key" />}
    </GraphWrapper>
  );
};

NetZeroChart.propTypes = {
  currentStep: PropTypes.number.isRequired,
  breakdownSteps: PropTypes.array.isRequired,
  heatingMethod: PropTypes.string.isRequired,
  interactiveBreakdown: PropTypes.shape({
    electricity: PropTypes.number.isRequired,
    heating: PropTypes.number.isRequired,
    car: PropTypes.number.isRequired,
    solar: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
  }).isRequired,
  interactive: PropTypes.bool.isRequired,
  hideNotations: PropTypes.bool.isRequired,
  cars: PropTypes.array.isRequired,
};

export default NetZeroChart;
